import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { creatorAPI, profileAPI, photoAPI } from '../services/api';
import LoadingAnimation from '../components/LoadingAnimation';
import './CreatorDashboard.css';

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightContentId = searchParams.get('highlight');
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [content, setContent] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [subscriptionPrice, setSubscriptionPrice] = useState(100);
  const [creatorBio, setCreatorBio] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Upload form state
  const [uploadData, setUploadData] = useState({
    contentType: 'PHOTO',
    file: null,
    caption: '',
    isExclusive: true
  });
  const [uploadPreview, setUploadPreview] = useState(null);

  useEffect(() => {
    loadData();
  }, []);
  
  useEffect(() => {
    // Scroll to highlighted content if specified
    if (highlightContentId && content.length > 0) {
      setTimeout(() => {
        const element = document.getElementById(`content-${highlightContentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.animation = 'highlight-pulse 2s ease-in-out';
        }
      }, 500);
    }
  }, [highlightContentId, content]);

  const loadData = async () => {
    try {
      setLoading(true);
      const profileRes = await profileAPI.getMyProfile();
      console.log('Profile loaded:', profileRes.data);
      console.log('isCreator:', profileRes.data.isCreator);
      
      if (!profileRes.data) {
        setError('Profile not found. Please complete your profile setup first.');
        return;
      }
      
      setProfile(profileRes.data);

      if (profileRes.data.isCreator) {
        const earningsRes = await creatorAPI.getCreatorEarnings();
        setEarnings(earningsRes.data);
        setSubscriptionPrice(profileRes.data.subscriptionPrice || 100);
        setCreatorBio(profileRes.data.creatorBio || '');
        
        // Load content and subscribers
        const contentRes = await creatorAPI.getMyContent();
        setContent(contentRes.data || []);
        
        const subscribersRes = await creatorAPI.getSubscribers();
        setSubscribers(subscribersRes.data || []);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load profile: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEnableCreator = async () => {
    // PHASE 1: Creator mode temporarily disabled
    setError('Creator mode is not available in Phase 1. Creator earnings will be enabled in Phase 2.');
  };

  const handleUpdateSettings = async () => {
    setEnabling(true);
    setError('');

    try {
      await creatorAPI.updateCreatorSettings(subscriptionPrice, creatorBio);
      setSuccess('Settings updated successfully!');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setEnabling(false);
    }
  };
  
  const handleUploadContent = async () => {
    if (!uploadData.file) {
      setError('Please select a file to upload');
      return;
    }
    
    // Validate file size (max 25MB for videos, 10MB for photos)
    const maxSize = uploadData.contentType === 'VIDEO' ? 25 * 1024 * 1024 : 10 * 1024 * 1024;
    if (uploadData.file.size > maxSize) {
      setError(`File too large. Max size: ${uploadData.contentType === 'VIDEO' ? '25MB' : '10MB'}`);
      return;
    }
    
    setUploading(true);
    setError('');
    
    try {
      // Upload file to Cloudinary via backend using the new creator content endpoint
      const uploadRes = await photoAPI.uploadCreatorContent(uploadData.file);
      const contentUrl = uploadRes.data.url;
      
      // Create content entry
      await creatorAPI.uploadContent(
        uploadData.contentType,
        contentUrl,
        contentUrl, // Use same URL as thumbnail
        uploadData.caption,
        uploadData.isExclusive
      );
      
      setSuccess('Content uploaded successfully!');
      setShowUploadModal(false);
      setUploadData({
        contentType: 'PHOTO',
        file: null,
        caption: '',
        isExclusive: true
      });
      setUploadPreview(null);
      loadData();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to upload content. Videos must be under 25MB.');
    } finally {
      setUploading(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({...uploadData, file});
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      return;
    }
    
    try {
      await creatorAPI.deleteContent(contentId);
      setSuccess('Content deleted successfully!');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete content');
    }
  };

  if (loading) {
    return (
      <div className="creator-dashboard">
        <LoadingAnimation />
      </div>
    );
  }

  if (profile?.gender !== 'FEMALE') {
    return (
      <div className="creator-dashboard">
        <div className="error-card">
          <h2>Creator Mode Not Available</h2>
          <p>Only female users can become creators.</p>
          <button onClick={() => navigate('/profile')} className="btn-primary">
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  if (!profile?.isCreator) {
    return (
      <div className="creator-dashboard">
        <div className="enable-creator-card">
          <div className="enable-icon">💎</div>
          <h2>Become a Creator</h2>
          <p>Start earning by offering exclusive content to your subscribers</p>
          
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="benefit-icon">💰</span>
              <span>Earn 80% of subscription fees</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">📸</span>
              <span>Share exclusive content</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">👥</span>
              <span>Build your fanbase</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">💳</span>
              <span>Monthly payouts</span>
            </div>
          </div>

          <button onClick={() => setShowEnableModal(true)} className="btn-enable-creator">
            Enable Creator Mode
          </button>
        </div>

        {showEnableModal && (
          <div className="modal-overlay" onClick={() => setShowEnableModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Enable Creator Mode</h3>
              
              <div className="form-group">
                <label>Monthly Subscription Price (Minimum R20)</label>
                <input
                  type="number"
                  value={subscriptionPrice}
                  onChange={(e) => setSubscriptionPrice(parseInt(e.target.value))}
                  min="20"
                  className="form-input"
                />
                <small>You'll earn 80% (R{Math.floor(subscriptionPrice * 0.8)}) per subscriber</small>
              </div>

              <div className="form-group">
                <label>Creator Bio</label>
                <textarea
                  value={creatorBio}
                  onChange={(e) => setCreatorBio(e.target.value)}
                  placeholder="Tell subscribers what they'll get..."
                  className="form-textarea"
                  rows="4"
                />
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="modal-actions">
                <button onClick={() => setShowEnableModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={handleEnableCreator} disabled={enabling} className="btn-primary">
                  {enabling ? 'Enabling...' : 'Enable Creator Mode'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="creator-dashboard">
      <div className="dashboard-header">
        <h1>Creator Dashboard</h1>
        <button onClick={() => navigate('/profile')} className="btn-secondary">
          Back to Profile
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Earnings Overview */}
      <div className="earnings-overview">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Pending</span>
            <div className="stat-icon">💰</div>
          </div>
          <p className="stat-value">R{earnings?.pendingEarnings || 0}</p>
          <span className="stat-subtitle">Ready to cash out</span>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Paid Out</span>
            <div className="stat-icon">✅</div>
          </div>
          <p className="stat-value">R{earnings?.paidEarnings || 0}</p>
          <span className="stat-subtitle">Total earned</span>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Subscribers</span>
            <div className="stat-icon">👥</div>
          </div>
          <p className="stat-value">{earnings?.totalSubscribers || 0}</p>
          <span className="stat-subtitle">Active fans</span>
        </div>

        <div className="stat-card highlight">
          <div className="stat-header">
            <span className="stat-label">Monthly</span>
            <div className="stat-icon">📈</div>
          </div>
          <p className="stat-value">R{earnings?.monthlyRevenue || 0}</p>
          <span className="stat-subtitle">Per month</span>
        </div>
      </div>

      {/* Early Payout Button */}
      {(earnings?.pendingEarnings || 0) >= 50 && (
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <button 
            onClick={() => navigate('/early-payout')} 
            className="btn-primary"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '15px 40px',
              fontSize: '16px',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            💰 Request Early Payout (R{earnings?.pendingEarnings || 0} Available)
          </button>
        </div>
      )}

      {/* Creator Settings */}
      <div className="settings-card">
        <div className="settings-header">
          <h2>Settings</h2>
        </div>
        
        <div className="form-grid">
          <div className="form-group">
            <label>Monthly Price</label>
            <div className="input-with-addon">
              <span className="input-addon">R</span>
              <input
                type="number"
                value={subscriptionPrice}
                onChange={(e) => setSubscriptionPrice(parseInt(e.target.value))}
                min="20"
                className="form-input"
              />
            </div>
            <small className="form-hint">
              You keep R{Math.floor(subscriptionPrice * 0.8)} per subscriber (80%)
            </small>
          </div>

          <div className="form-group full-width">
            <label>What You Offer</label>
            <textarea
              value={creatorBio}
              onChange={(e) => setCreatorBio(e.target.value)}
              placeholder="Tell subscribers what they get..."
              className="form-textarea"
              rows="3"
            />
          </div>
        </div>

        <button onClick={handleUpdateSettings} disabled={enabling} className="btn-save-settings">
          {enabling ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Content Management */}
      <div className="content-card">
        <div className="content-header">
          <h2>My Content ({content.length})</h2>
          <button onClick={() => setShowUploadModal(true)} className="btn-upload">
            📸 Upload Content
          </button>
        </div>
        
        {content.length === 0 ? (
          <div className="empty-content">
            <p>No content yet. Upload your first exclusive content!</p>
          </div>
        ) : (
          <div className="content-grid">
            {content.map((item) => (
              <div key={item.id} id={`content-${item.id}`} className="content-item">
                <div className="content-thumbnail">
                  <img src={item.contentUrl} alt={item.caption || 'Content'} />
                  {item.isExclusive && <span className="exclusive-badge">🔒 Exclusive</span>}
                </div>
                <div className="content-info">
                  <p className="content-caption">{item.caption || 'No caption'}</p>
                  <div className="content-stats">
                    <span>👁️ {item.viewsCount}</span>
                    <span>❤️ {item.likesCount}</span>
                  </div>
                </div>
                <button 
                  onClick={() => handleDeleteContent(item.id)} 
                  className="btn-delete-content"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subscribers List */}
      <div className="subscribers-card">
        <div className="subscribers-header">
          <h2>Subscribers ({subscribers.length})</h2>
        </div>
        
        {subscribers.length === 0 ? (
          <div className="empty-subscribers">
            <p>No subscribers yet. Share your profile to get started!</p>
          </div>
        ) : (
          <div className="subscribers-list">
            {subscribers.map((sub) => (
              <div key={sub.id} className="subscriber-item">
                <div className="subscriber-avatar">
                  {sub.creatorPhotoUrl ? (
                    <img src={sub.creatorPhotoUrl} alt={sub.creatorName} />
                  ) : (
                    <div className="avatar-placeholder">👤</div>
                  )}
                </div>
                <div className="subscriber-info">
                  <p className="subscriber-name">{sub.creatorName || 'Anonymous'}</p>
                  <p className="subscriber-date">
                    Subscribed {new Date(sub.subscriptionStartDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="subscriber-status">
                  <span className="days-remaining">{sub.daysRemaining} days left</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payout Info */}
      <div className="payout-card">
        <div className="payout-header">
          <div className="payout-icon">💳</div>
          <div>
            <h3>Payouts</h3>
            <p>Withdraw to your bank account</p>
          </div>
        </div>
        
        <div className="payout-details">
          <div className="payout-row">
            <span className="payout-label">Minimum:</span>
            <span className="payout-value">R500</span>
          </div>
          <div className="payout-row">
            <span className="payout-label">Your balance:</span>
            <span className="payout-value highlight">R{earnings?.pendingEarnings || 0}</span>
          </div>
        </div>

        {(earnings?.pendingEarnings || 0) >= 500 ? (
          <button className="btn-request-payout">
            Request Payout
          </button>
        ) : (
          <div className="payout-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${Math.min(((earnings?.pendingEarnings || 0) / 500) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="progress-text">
              R{500 - (earnings?.pendingEarnings || 0)} more needed
            </p>
          </div>
        )}
      </div>
      
      {/* Upload Content Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Upload Content</h3>
            
            <div className="form-group">
              <label>Content Type</label>
              <select
                value={uploadData.contentType}
                onChange={(e) => setUploadData({...uploadData, contentType: e.target.value})}
                className="form-input"
              >
                <option value="PHOTO">Photo</option>
                <option value="VIDEO">Video</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Select File</label>
              <input
                type="file"
                accept={uploadData.contentType === 'PHOTO' ? 'image/*' : 'video/*'}
                onChange={handleFileSelect}
                className="form-input"
              />
              {uploadPreview && (
                <div className="upload-preview">
                  {uploadData.contentType === 'PHOTO' ? (
                    <img src={uploadPreview} alt="Preview" />
                  ) : (
                    <video src={uploadPreview} controls />
                  )}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Caption (Optional)</label>
              <textarea
                value={uploadData.caption}
                onChange={(e) => setUploadData({...uploadData, caption: e.target.value})}
                placeholder="Add a caption..."
                className="form-textarea"
                rows="3"
              />
            </div>
            
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={uploadData.isExclusive}
                  onChange={(e) => setUploadData({...uploadData, isExclusive: e.target.checked})}
                />
                <span>Exclusive content (subscribers only)</span>
              </label>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="modal-actions">
              <button onClick={() => setShowUploadModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleUploadContent} disabled={uploading} className="btn-primary">
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorDashboard;
